import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ClipboardList, Plus, Pencil, Trash2, Loader2, Calendar, Building2, Upload, Download, CheckCircle2, AlertTriangle, Eye, ChevronDown, ChevronRight, User, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { orderService, type OrderImportPreviewResult, type OrderImportPreviewRow } from '../services/orderService';
import { orderAssignmentService, type OrderAssignment } from '../services/orderAssignmentService';
import { clientSupplierService } from '../services/clientSupplierService';
import { userService } from '../services/userService';
import { Order, ClientSupplier, ClientType } from '@timesheet/schemas';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Alert } from '../components/ui/alert';
import { PageTransition } from '../components/PageTransition';
import { useMobile } from '../hooks/use-mobile';
import { PullToRefresh } from '../components/shared/pull-to-refresh';
import { SwipeableRow, createEditAction, createDeleteAction } from '../components/shared/swipeable-row';
import { BottomSheet } from '../components/shared/bottom-sheet';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { getRoleLabel } from '../lib/role-labels';

const initialFormData = {
  code: '',
  description: '',
  startDate: format(new Date(), 'yyyy-MM-dd'),
  endDate: format(new Date(), 'yyyy-MM-dd'),
  plafond: '',
  clientId: 0,
  supplierId: null as number | null,
};

const formatPlafondForUi = (value: number): string => new Intl.NumberFormat('it-IT', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(value);

const parseItalianPlafond = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const compact = trimmed.replace(/\s/g, '');
  const italianDecimalPattern = /^(\d+|\d{1,3}(\.\d{3})+)(,\d{1,2})?$/;
  if (!italianDecimalPattern.test(compact)) {
    throw new Error('invalid-plafond-format');
  }

  const normalized = compact.replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) {
    throw new Error('invalid-plafond-format');
  }

  return parsed;
};

const waitForUiPaint = (): Promise<void> => new Promise((resolve) => {
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => resolve());
    return;
  }
  setTimeout(resolve, 0);
});

type OrderUiStatus = 'active' | 'upcoming' | 'expired';

const parseLocalDate = (input: string): Date => {
  const [year, month, day] = input.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const getToday = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const getOrderStatus = (order: Order): OrderUiStatus => {
  const today = getToday();
  const startDate = parseLocalDate(order.startDate);
  const endDate = parseLocalDate(order.endDate);

  if (startDate > today) {
    return 'upcoming';
  }
  if (endDate < today) {
    return 'expired';
  }
  return 'active';
};

const assignmentPalette = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-violet-500', 'bg-rose-500', 'bg-cyan-500'];

const getAssignmentColorClass = (userId: number): string => assignmentPalette[Math.abs(userId) % assignmentPalette.length];

const countActiveSegments = (assignments: OrderAssignment[]): number => {
  const today = getToday();
  return assignments.filter((assignment) => {
    const startDate = parseLocalDate(assignment.startDate);
    const endDate = assignment.endDate ? parseLocalDate(assignment.endDate) : new Date(2099, 11, 31);
    return startDate <= today && today <= endDate;
  }).length;
};

const getVisibleAssignmentDetails = (assignments: OrderAssignment[]): OrderAssignment[] => {
  const today = getToday();
  const assignmentsByUser = assignments.reduce<Map<number, OrderAssignment[]>>((accumulator, assignment) => {
    if (!accumulator.has(assignment.userId)) {
      accumulator.set(assignment.userId, []);
    }
    accumulator.get(assignment.userId)!.push(assignment);
    return accumulator;
  }, new Map<number, OrderAssignment[]>());

  return Array.from(assignmentsByUser.values())
    .map((userAssignments) => {
      const sortedAssignments = [...userAssignments].sort(
        (left, right) => new Date(left.startDate).getTime() - new Date(right.startDate).getTime()
      );
      const currentAssignment = sortedAssignments.find((assignment) => {
        const startDate = parseLocalDate(assignment.startDate);
        const endDate = assignment.endDate ? parseLocalDate(assignment.endDate) : new Date(2099, 11, 31);
        return startDate <= today && today <= endDate;
      });

      if (currentAssignment) {
        return currentAssignment;
      }

      return sortedAssignments.find((assignment) => parseLocalDate(assignment.startDate) > today) ?? null;
    })
    .filter((assignment): assignment is OrderAssignment => assignment !== null);
};

// Helper to reduce month labels only on mobile; desktop keeps all month ticks visible
const getFilteredMonthTicks = (monthTicks: Date[], isMobile: boolean): Set<string> => {
  if (!isMobile) {
    return new Set(monthTicks.map((d) => `${d.getFullYear()}-${d.getMonth()}`));
  }

  if (monthTicks.length <= 3) {
    return new Set(monthTicks.map((d) => `${d.getFullYear()}-${d.getMonth()}`));
  }

  const visibleIndices = new Set<number>();
  visibleIndices.add(0); // First
  visibleIndices.add(Math.floor(monthTicks.length / 2)); // Middle
  visibleIndices.add(monthTicks.length - 1); // Last

  return new Set(
    Array.from(visibleIndices).map((idx) => {
      const d = monthTicks[idx];
      return `${d.getFullYear()}-${d.getMonth()}`;
    })
  );
};

const OrderManager: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const [orders, setOrders] = useState<Order[]>([]);
  const [orderAssignments, setOrderAssignments] = useState<OrderAssignment[]>([]);
  const [clients, setClients] = useState<ClientSupplier[]>([]);
  const [suppliers, setSuppliers] = useState<ClientSupplier[]>([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedImportFile, setSelectedImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<OrderImportPreviewResult | null>(null);
  const [importClientMappings, setImportClientMappings] = useState<Record<string, number>>({});
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importDialogError, setImportDialogError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{ total: number; created: number; errors: string[] } | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [invalidPreviewRow, setInvalidPreviewRow] = useState<OrderImportPreviewRow | null>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const isImportBusy = previewLoading || importing;
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isUserFilterEnabled, setIsUserFilterEnabled] = useState(false);
  const [filterUserId, setFilterUserId] = useState<number | null>(null);
  const [users, setUsers] = useState<{ id: number; name: string }[]>([]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await orderService.getAll();
      setOrders(data);
    } catch (err) {
      setError(t('pages.orders.errors.fetch'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchClientSuppliers = useCallback(async () => {
    try {
      const data = await clientSupplierService.getAll();
      setClients(data.filter((cs: ClientSupplier) => (cs.type === ClientType.CLIENT || cs.type === ClientType.BOTH) && cs.isActive !== false));
      setSuppliers(data.filter((cs: ClientSupplier) => (cs.type === ClientType.SUPPLIER || cs.type === ClientType.BOTH) && cs.isActive !== false));
    } catch (err) {
      setError(t('pages.orders.errors.fetchClientSuppliers'));
    }
  }, [t]);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await userService.getAll();
      setUsers(data);
    } catch {
      // non bloccante
    }
  }, []);

  const fetchOrderAssignments = useCallback(async () => {
    try {
      const data = await orderAssignmentService.getAll();
      setOrderAssignments(data);
    } catch {
      setError(t('orderAssignments.errors.fetch', 'Failed to fetch assignments'));
    }
  }, [t]);

  useEffect(() => {
    fetchOrders();
    fetchClientSuppliers();
    fetchOrderAssignments();
    fetchUsers();
  }, [fetchOrders, fetchClientSuppliers, fetchOrderAssignments, fetchUsers]);

  const handleOpen = () => {
    setOpen(true);
    setFormData(initialFormData);
    setEditingOrder(null);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialFormData);
    setEditingOrder(null);
    setError(null);
  };

  const resetImportDialog = useCallback(() => {
    setSelectedImportFile(null);
    setImportPreview(null);
    setImportClientMappings({});
    setPreviewLoading(false);
    setImporting(false);
    setImportDialogError(null);
    setIsDraggingFile(false);
    setInvalidPreviewRow(null);
    if (importFileInputRef.current) {
      importFileInputRef.current.value = '';
    }
  }, []);

  const handleOpenImport = () => {
    setImportOpen(true);
    resetImportDialog();
  };

  const handleCloseImport = () => {
    setImportOpen(false);
    resetImportDialog();
  };

  const previewImportFile = useCallback(async (file: File, mappings: Record<string, number>) => {
    setSelectedImportFile(file);
    setPreviewLoading(true);
    setImportDialogError(null);
    setImportPreview(null);

    // Let React paint the loading state before starting heavier work.
    await waitForUiPaint();

    try {
      const preview = await orderService.previewImport(file, mappings);
      setImportPreview(preview);
    } catch {
      setImportDialogError(t('pages.orders.errors.import', 'Failed to import CSV file'));
    } finally {
      setPreviewLoading(false);
    }
  }, [t]);

  const handleImportFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isImportBusy) return;
    const file = event.target.files?.[0];
    if (!file) return;
    await previewImportFile(file, importClientMappings);
  };

  const handleImportDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingFile(false);
    if (isImportBusy) return;
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    await previewImportFile(file, importClientMappings);
  };

  const handleDownloadImportTemplate = async () => {
    try {
      const blob = await orderService.downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'orders-import-template.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setImportDialogError(t('pages.orders.errors.import', 'Failed to import CSV file'));
    }
  };

  const handleRefreshPreviewWithMappings = async () => {
    if (!selectedImportFile) return;
    await previewImportFile(selectedImportFile, importClientMappings);
  };

  const handleImportConfirm = async () => {
    if (!selectedImportFile) return;

    try {
      setImporting(true);
      await waitForUiPaint();
      const result = await orderService.import(selectedImportFile, importClientMappings);
      setImportResult(result);
      if (result.created > 0) {
        await fetchOrders();
      }
      handleCloseImport();
    } catch {
      setImportDialogError(t('pages.orders.errors.import', 'Failed to import CSV file'));
    } finally {
      setImporting(false);
    }
  };

  const getInvalidRowMessage = (row: OrderImportPreviewRow): string => {
    if (!row.error) {
      return t('pages.orders.importErrors.generic', 'Some fields are invalid. Check and try again.');
    }

    const mapOrderImportErrorToFriendlyMessage = (error: string): string => {
      if (/^client:/i.test(error)) {
        return t('pages.orders.importErrors.clientNotFound', 'Client not found. Select an existing client for this row.');
      }
      if (/^dateRange:/i.test(error)) {
        return t('pages.orders.importErrors.invalidDateRange', 'End date cannot be before start date.');
      }
      if (/^plafond:/i.test(error)) {
        return t('pages.orders.importErrors.invalidPlafond', 'Plafond must be a valid non-negative number.');
      }
      if (/^startDate:/i.test(error) || /^endDate:/i.test(error)) {
        return t('pages.orders.importErrors.invalidDateFormat', 'Dates must use YYYY-MM-DD format.');
      }
      if (/^code:/i.test(error)) {
        return t('pages.orders.importErrors.codeRequired', 'Order code is required.');
      }
      if (/^description:/i.test(error)) {
        return t('pages.orders.importErrors.descriptionRequired', 'Description is required.');
      }
      if (/^duplicateCode:/i.test(error)) {
        return t('pages.orders.importErrors.duplicateCode', 'Order code is duplicated in the CSV or already exists.');
      }
      return t('pages.orders.importErrors.generic', 'Some fields are invalid. Check and try again.');
    };

    return row.error
      .split(';')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => mapOrderImportErrorToFriendlyMessage(part))
      .join(' ');
  };

  const rowNeedsClientMapping = (row: OrderImportPreviewRow) => /^client:/i.test(row.error ?? '');

  const getFilteredOrders = (): Order[] => {
    let result = orders;

    if (isUserFilterEnabled && filterUserId !== null) {
      const orderIdsForUser = new Set(
        orderAssignments
          .filter((a) => a.userId === filterUserId)
          .map((a) => a.orderId)
      );
      result = result.filter((o) => orderIdsForUser.has(o.id));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(
        (o) =>
          o.code.toLowerCase().includes(q) ||
          o.description.toLowerCase().includes(q) ||
          (o.client?.name ?? '').toLowerCase().includes(q)
      );
    }

    return result;
  };

  const handleEdit = (order: Order) => {
    setEditingOrder(order);
    setFormData({
      code: order.code,
      description: order.description,
      startDate: format(new Date(order.startDate), 'yyyy-MM-dd'),
      endDate: format(new Date(order.endDate), 'yyyy-MM-dd'),
      plafond: order.plafond != null ? formatPlafondForUi(Number(order.plafond)) : '',
      clientId: order.clientId,
      supplierId: order.supplierId ?? null,
    });
    setOpen(true);
  };

  const handleDelete = (id: number) => {
    setDeleteTargetId(id);
  };

  const handleOpenOrderDetails = (orderId: number) => {
    navigate(`/order-assignments?tab=by-order&orderId=${orderId}&from=orders`);
  };

  const toggleExpandedOrder = (orderId: number) => {
    setExpandedOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteTargetId === null) return;
    setDeleting(true);
    try {
      await orderService.delete(deleteTargetId);
      setDeleteTargetId(null);
      await fetchOrders();
    } catch (err) {
      // Toast shown automatically by global interceptor
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const parsedPlafond = parseItalianPlafond(formData.plafond);
      const payload = {
        ...formData,
        plafond: parsedPlafond,
      };

      if (editingOrder) {
        await orderService.update(editingOrder.id, payload);
      } else {
        await orderService.create(payload);
      }
      handleClose();
      await fetchOrders();
      await fetchOrderAssignments();
    } catch (err) {
      if (err instanceof Error && err.message === 'invalid-plafond-format') {
        setError(t('pages.orders.errors.invalidPlafondFormat', 'Il plafond deve usare la virgola per i centesimi e il punto solo per le migliaia.'));
      }
      // Toast shown automatically by global interceptor
    } finally {
      setSaving(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.code &&
      formData.description &&
      formData.startDate &&
      formData.endDate &&
      formData.clientId
    );
  };

  const renderOrderStatusBadge = (order: Order) => {
    const status = getOrderStatus(order);

    if (status === 'active') {
      return (
        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-0 text-xs">
          {t('orderAssignments.status.active', 'Active')}
        </Badge>
      );
    }

    if (status === 'upcoming') {
      return (
        <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-0 text-xs">
          {t('orderAssignments.status.upcoming', 'Upcoming')}
        </Badge>
      );
    }

    return <Badge variant="secondary" className="text-xs">{t('orderAssignments.status.expired', 'Expired')}</Badge>;
  };

  const renderOrdersGantt = () => {
    const dayMs = 24 * 60 * 60 * 1000;

    return (
      <div className="space-y-4">
        {getFilteredOrders().map((order) => {
          const assignmentsForOrder = orderAssignments
            .filter((assignment) => assignment.orderId === order.id)
            .sort((left, right) => new Date(left.startDate).getTime() - new Date(right.startDate).getTime());
          const visibleAssignmentDetails = getVisibleAssignmentDetails(assignmentsForOrder);
          const isExpanded = expandedOrderIds.has(order.id);

          return (
            <Card key={`gantt-order-${order.id}`}>
              <CardContent className="p-4 space-y-4">
                {/* Collapsed Header */}
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-2 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => toggleExpandedOrder(order.id)}
                        className="inline-flex items-center gap-1 text-left font-semibold hover:underline"
                      >
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        <span className="font-mono">{order.code}</span>
                      </button>
                      {renderOrderStatusBadge(order)}
                    </div>
                    <p className="text-sm text-muted-foreground">{order.description}</p>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {order.client?.name || '-'}
                      </span>
                      {order.supplier && (
                        <span className="flex items-center gap-1">
                          <Building2 className="h-3 w-3 text-muted-foreground/60" />
                          {order.supplier.name}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(order.startDate), 'dd/MM/yy')} - {format(new Date(order.endDate), 'dd/MM/yy')}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => handleOpenOrderDetails(order.id)}>
                      <Eye className="h-4 w-4 mr-1" />
                      {t('orderAssignments.actions.openOrderDetails', 'Open order details')}
                    </Button>
                  </div>
                </div>

                {/* Expanded Gantt Section */}
                {isExpanded && (
                  <div className="rounded-lg border bg-muted/20 p-3">
                    {assignmentsForOrder.length === 0 ? (
                      <p className="text-sm text-muted-foreground">{t('orderAssignments.empty.byOrder', 'This order has no user assignments.')}</p>
                    ) : (
                      <div className="space-y-3">
                        {/* Timeline Header with Month Ticks */}
                        <div>
                          <div className="grid gap-2 lg:grid-cols-[260px_1fr] lg:items-center">
                            <div />
                            <div className="relative h-8 rounded border bg-background/60 overflow-hidden">
                              {(() => {
                                const orderStart = parseLocalDate(order.startDate);
                                const orderEnd = parseLocalDate(order.endDate);
                                const totalDays = Math.max(1, Math.floor((orderEnd.getTime() - orderStart.getTime()) / dayMs) + 1);
                                const monthTicks: Date[] = [];
                                const tickDate = new Date(orderStart.getFullYear(), orderStart.getMonth(), 1);
                                while (tickDate <= orderEnd) {
                                  monthTicks.push(new Date(tickDate));
                                  tickDate.setMonth(tickDate.getMonth() + 1);
                                }
                                const visibleMonths = getFilteredMonthTicks(monthTicks, isMobile);
                                
                                return monthTicks.map((tick) => {
                                  const offsetDays = Math.floor((tick.getTime() - orderStart.getTime()) / dayMs);
                                  const leftPct = Math.max(0, Math.min(100, (offsetDays / totalDays) * 100));
                                  const key = `${tick.getFullYear()}-${tick.getMonth()}`;
                                  const isVisible = visibleMonths.has(key);
                                  
                                  return (
                                    <div
                                      key={`order-${order.id}-tick-${key}`}
                                      className="absolute top-0 h-full border-l border-muted-foreground/20"
                                      style={{ left: `${leftPct}%` }}
                                    >
                                      {isVisible && (
                                        <span className="absolute left-1 top-1 text-[10px] font-medium text-muted-foreground whitespace-nowrap">
                                          {format(tick, 'MMM yy')}
                                        </span>
                                      )}
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          </div>
                        </div>

                        {/* User Lanes */}
                        {(() => {
                          const assignmentsByUser = assignmentsForOrder.reduce<Map<number, OrderAssignment[]>>((accumulator, assignment) => {
                            const key = assignment.userId;
                            if (!accumulator.has(key)) {
                              accumulator.set(key, []);
                            }
                            accumulator.get(key)!.push(assignment);
                            return accumulator;
                          }, new Map<number, OrderAssignment[]>());
                          const userLanes = Array.from(assignmentsByUser.values()).map((items) =>
                            items.sort((left, right) => new Date(left.startDate).getTime() - new Date(right.startDate).getTime())
                          );
                          const orderStart = parseLocalDate(order.startDate);
                          const orderEnd = parseLocalDate(order.endDate);
                          const totalDays = Math.max(1, Math.floor((orderEnd.getTime() - orderStart.getTime()) / dayMs) + 1);
                          const monthTicks: Date[] = [];
                          const tickDate = new Date(orderStart.getFullYear(), orderStart.getMonth(), 1);
                          while (tickDate <= orderEnd) {
                            monthTicks.push(new Date(tickDate));
                            tickDate.setMonth(tickDate.getMonth() + 1);
                          }

                          return userLanes.map((lane) => {
                            const firstAssignment = lane[0];
                            const laneColorClass = getAssignmentColorClass(firstAssignment.userId);

                            return (
                              <div key={`order-gantt-user-${order.id}-${firstAssignment.userId}`} className="grid gap-2 lg:grid-cols-[260px_1fr] lg:items-center">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className={`inline-block h-2.5 w-2.5 rounded-full ${laneColorClass}`} />
                                  <span className="truncate font-medium">{firstAssignment.user?.name || t('orderAssignments.columns.user', 'User')}</span>
                                  <span className="text-xs text-muted-foreground truncate">{firstAssignment.user?.role ? getRoleLabel(firstAssignment.user.role, t) : ''}</span>
                                </div>
                                <div className="relative h-11 rounded bg-background border overflow-hidden">
                                  {monthTicks.map((tick) => {
                                    const offsetDays = Math.floor((tick.getTime() - orderStart.getTime()) / dayMs);
                                    const leftPct = Math.max(0, Math.min(100, (offsetDays / totalDays) * 100));
                                    const key = `${tick.getFullYear()}-${tick.getMonth()}`;
                                    
                                    return (
                                      <div
                                        key={`lane-${order.id}-${firstAssignment.userId}-${key}`}
                                        className="absolute top-0 h-full border-l border-muted-foreground/15"
                                        style={{ left: `${leftPct}%` }}
                                      />
                                    );
                                  })}

                                  {lane.map((assignment) => {
                                    const segmentStart = parseLocalDate(assignment.startDate);
                                    const rawEnd = assignment.endDate ? parseLocalDate(assignment.endDate) : orderEnd;
                                    const clampedStart = segmentStart < orderStart ? orderStart : segmentStart;
                                    const clampedEnd = rawEnd > orderEnd ? orderEnd : rawEnd;
                                    const startOffsetDays = Math.floor((clampedStart.getTime() - orderStart.getTime()) / dayMs);
                                    const segmentDays = Math.max(1, Math.floor((clampedEnd.getTime() - clampedStart.getTime()) / dayMs) + 1);
                                    const leftPct = (startOffsetDays / totalDays) * 100;
                                    const widthPct = Math.max((segmentDays / totalDays) * 100, 1.5);
                                    const colorClass = getOrderStatus(order) === 'active' && getToday() >= clampedStart && getToday() <= clampedEnd
                                      ? laneColorClass
                                      : 'bg-slate-300 dark:bg-slate-700';

                                    return (
                                      <div
                                        key={`order-gantt-${assignment.id}`}
                                        className={`absolute top-1/2 h-4 -translate-y-1/2 rounded ${colorClass}`}
                                        style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                                        title={`${assignment.user?.name || ''} | ${format(new Date(assignment.startDate), 'dd/MM/yyyy')} - ${assignment.endDate ? format(new Date(assignment.endDate), 'dd/MM/yyyy') : t('orderAssignments.ongoing', 'Ongoing')}`}
                                      />
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {/* Assignment Details when expanded */}
                {isExpanded && visibleAssignmentDetails.length > 0 && (
                  <div className="space-y-2">
                    {visibleAssignmentDetails.map((assignment) => (
                      <div key={`order-gantt-details-${assignment.id}`} className="rounded border p-3 text-sm">
                        <div className="flex flex-wrap items-center gap-2 justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`inline-block h-2.5 w-2.5 rounded-full ${getAssignmentColorClass(assignment.userId)}`} />
                            <span className="font-medium truncate">{assignment.user?.name || t('orderAssignments.columns.user', 'User')}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(assignment.startDate), 'dd/MM/yyyy')} - {assignment.endDate ? format(new Date(assignment.endDate), 'dd/MM/yyyy') : t('orderAssignments.ongoing', 'Ongoing')}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
                          <span>{t('orderAssignments.form.sellRate', 'Sell Rate')}: {assignment.sellRate != null ? `€${Number(assignment.sellRate).toFixed(2)}` : '-'}</span>
                          <span>{t('orderAssignments.form.costRate', 'Cost Rate')}: {assignment.costRate != null ? `€${Number(assignment.costRate).toFixed(2)}` : '-'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <PageTransition>
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <ClipboardList className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('pages.orders.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('pages.orders.subtitle', 'Manage work orders')}
            </p>
          </div>
        </div>

        <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:flex-nowrap">
          <Button variant="outline" onClick={handleOpenImport} className="flex-1 gap-2 sm:flex-none">
            <Upload className="h-4 w-4" />
            {t('pages.orders.actions.import')}
          </Button>

          <Button onClick={handleOpen} className="flex-1 bg-blue-500 hover:bg-blue-600 gap-2 sm:flex-none">
            <Plus className="h-4 w-4" />
            {t('pages.orders.actions.create')}
          </Button>
        </div>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {importResult && (
        <Alert variant={importResult.errors.length > 0 ? 'warning' : 'success'}>
          <div className="space-y-1">
            <p className="font-medium">{t('pages.orders.importResults.title')}:</p>
            <p>
              {t('pages.orders.importResults.total')}: {importResult.total},{' '}
              {t('pages.orders.importResults.created')}: {importResult.created}
            </p>
          </div>
        </Alert>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('pages.orders.search', 'Cerca per codice, descrizione o cliente...')}
            className="pl-9 pr-8"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {isUserFilterEnabled && (
            <Select
              value={filterUserId !== null ? String(filterUserId) : ''}
              onValueChange={(v) => setFilterUserId(Number(v))}
            >
              <SelectTrigger className="w-[220px]">
                <User className="mr-2 h-4 w-4 text-muted-foreground" />
                <SelectValue placeholder={t('pages.orders.selectUserFilter', 'Seleziona utente...')} />
              </SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.id} value={String(u.id)}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="flex items-center gap-2 rounded-md border bg-background px-3 py-2">
            <Label htmlFor="orders-filter-by-user" className="text-sm font-normal">
              {t('pages.orders.filterByUser', 'Per utente')}
            </Label>
            <Switch
              id="orders-filter-by-user"
              checked={isUserFilterEnabled}
              onCheckedChange={(checked) => {
                setIsUserFilterEnabled(checked);
                if (checked) {
                  setFilterUserId((prev) => prev ?? users[0]?.id ?? null);
                }
              }}
            />
          </div>
        </div>
      </div>
      <PullToRefresh onRefresh={fetchOrders} disabled={!isMobile}>
        {renderOrdersGantt()}
      </PullToRefresh>

      <BottomSheet
        open={importOpen}
        onClose={handleCloseImport}
        size="wide"
        title={t('pages.orders.actions.import')}
        description={t('pages.orders.importDescription', 'Import orders from CSV file.')}
      >
        <div className="relative space-y-4">
          {isImportBusy && (
            <div className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-background/70 backdrop-blur-[1px]">
              <div className="inline-flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm shadow-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                {previewLoading
                  ? t('pages.orders.previewing', 'Validating file...')
                  : t('pages.orders.importing', 'Importing...')}
              </div>
            </div>
          )}
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            ref={importFileInputRef}
            onChange={handleImportFileUpload}
          />

          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <div className="flex justify-center">
              <Button variant="outline" onClick={handleDownloadImportTemplate} className="gap-2">
                <Download className="h-4 w-4" />
                {t('pages.orders.downloadTemplate', 'Download template')}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              {t('pages.orders.templateHint', 'Usa il template di esempio: contiene già intestazioni e righe dimostrative compatibili con l\'importazione.')}
            </p>
          </div>

          <div
            onDragOver={(event) => {
              event.preventDefault();
              setIsDraggingFile(true);
            }}
            onDragLeave={() => setIsDraggingFile(false)}
            onDrop={handleImportDrop}
            className={`rounded-xl border-2 border-dashed p-6 text-center transition-colors ${isDraggingFile ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/10' : 'border-muted-foreground/25'}`}
          >
            <Upload className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="font-medium">{t('pages.orders.dropzoneTitle', 'Drop your CSV file here')}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedImportFile
                ? t('pages.orders.selectedFile', 'Selected file: {{name}}', { name: selectedImportFile.name })
                : t('pages.orders.dropzoneDescription', 'Upload the CSV file to preview before import.')}
            </p>
            <div className="mt-4 flex justify-center">
              <Button variant="outline" onClick={() => importFileInputRef.current?.click()} className="gap-2" disabled={isImportBusy}>
                <Upload className="h-4 w-4" />
                {t('pages.orders.selectFile', 'Choose CSV file')}
              </Button>
            </div>
          </div>

          {importDialogError && <Alert variant="error">{importDialogError}</Alert>}

          {previewLoading && (
            <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('pages.orders.previewing', 'Validating file...')}
            </div>
          )}

          {importPreview && !previewLoading && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('pages.orders.importResults.total')}</p>
                    <p className="text-2xl font-semibold">{importPreview.total}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('pages.orders.validRows', 'Valid')}</p>
                    <p className="text-2xl font-semibold text-emerald-600">{importPreview.valid}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('pages.orders.invalidRows', 'Invalid')}</p>
                    <p className="text-2xl font-semibold text-amber-600">{importPreview.errors.length}</p>
                  </CardContent>
                </Card>
              </div>

              {importPreview.unresolvedClients.length > 0 && (
                <div className="rounded-lg border p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium">{t('pages.orders.clientMappingTitle', 'Resolve unknown clients')}</p>
                    <Button variant="outline" size="sm" onClick={handleRefreshPreviewWithMappings}>
                      {t('pages.orders.validateMappings', 'Validate mapping')}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('pages.orders.clientMappingHint', 'Select an existing client for each unknown value found in the CSV.')}
                  </p>
                  <div className="space-y-2">
                    {importPreview.unresolvedClients.map((name) => (
                      <div key={name} className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-2 items-center">
                        <p className="text-sm font-medium truncate">{name}</p>
                        <Select
                          value={importClientMappings[name] ? String(importClientMappings[name]) : ''}
                          onValueChange={(value) => setImportClientMappings((prev) => ({ ...prev, [name]: Number(value) }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('pages.orders.selectClient', 'Select client...')} />
                          </SelectTrigger>
                          <SelectContent>
                            {clients.map((client) => (
                              <SelectItem key={client.id} value={String(client.id)}>
                                {client.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>{t('pages.orders.columns.code')}</TableHead>
                      <TableHead>{t('pages.orders.columns.description', 'Descrizione')}</TableHead>
                      <TableHead>{t('pages.orders.columns.client')}</TableHead>
                      <TableHead>{t('pages.orders.columns.startDate', 'Inizio')}</TableHead>
                      <TableHead>{t('pages.orders.columns.endDate', 'Fine')}</TableHead>
                      <TableHead>{t('pages.orders.columns.plafond', 'Plafond')}</TableHead>
                      <TableHead>{t('common.status', 'Status')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importPreview.rows.map((row) => (
                      <TableRow key={row.rowNumber}>
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell className="font-mono text-xs">{row.code || '—'}</TableCell>
                        <TableCell className="max-w-[180px] truncate">{row.description || '—'}</TableCell>
                        <TableCell>
                          <span className={rowNeedsClientMapping(row) ? 'text-amber-600 font-medium' : ''}>
                            {row.clientName || '—'}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{row.startDate || '—'}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{row.endDate || '—'}</TableCell>
                        <TableCell className="font-mono text-xs">{row.plafond || '—'}</TableCell>
                        <TableCell>
                          {row.valid ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600 text-sm">
                              <CheckCircle2 className="h-4 w-4" />
                              {t('pages.orders.rowValid', 'Valid')}
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setInvalidPreviewRow(row)}
                              className="inline-flex items-center gap-1 text-amber-600 text-sm underline-offset-2 hover:underline"
                            >
                              <AlertTriangle className="h-4 w-4" />
                              {t('pages.orders.rowInvalid', 'Invalid')}
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {importPreview.errors.length === 0 && importPreview.total > importPreview.rows.length && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-sm text-muted-foreground">
                          {t('pages.orders.previewContinues', 'Partial preview')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={handleCloseImport} disabled={importing} className="flex-1">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleImportConfirm}
              disabled={
                !selectedImportFile ||
                !importPreview ||
                importPreview.valid === 0 ||
                importing ||
                importPreview.unresolvedClients.some((name) => !importClientMappings[name])
              }
              className="flex-1 bg-blue-500 hover:bg-blue-600"
            >
              {importing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('pages.orders.importing', 'Importing...')}
                </>
              ) : (
                t('pages.orders.confirmImport', 'Import valid rows')
              )}
            </Button>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet
        open={Boolean(invalidPreviewRow)}
        onClose={() => setInvalidPreviewRow(null)}
        title={t('pages.orders.invalidRowDialogTitle', 'Row error details')}
        description={invalidPreviewRow ? t('pages.orders.invalidRowDialogDescription', 'Row {{row}} is invalid', { row: invalidPreviewRow.rowNumber }) : undefined}
      >
        <div className="space-y-4">
          {invalidPreviewRow && (
            <Alert variant="warning">
              {getInvalidRowMessage(invalidPreviewRow)}
            </Alert>
          )}
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setInvalidPreviewRow(null)}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </BottomSheet>

      {/* Create/Edit Form - BottomSheet on mobile, Modal on desktop */}
      <BottomSheet
        open={open}
        onClose={handleClose}
        title={editingOrder ? t('pages.orders.actions.edit') : t('pages.orders.actions.create')}
        description={
          editingOrder
            ? t('pages.orders.editDescription', 'Update order information')
            : t('pages.orders.createDescription', 'Add a new work order')
        }
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="code">{t('pages.orders.columns.code')}</Label>
            <Input
              id="code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              placeholder="ORD-2026-001"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t('pages.orders.columns.description')}</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('pages.orders.descriptionPlaceholder', 'Enter order description...')}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">{t('pages.orders.columns.startDate')}</Label>
              <Input
                id="startDate"
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="endDate">{t('pages.orders.columns.endDate')}</Label>
              <Input
                id="endDate"
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="plafond">{t('pages.orders.columns.plafond', 'Plafond')}</Label>
            <Input
              id="plafond"
              type="text"
              inputMode="decimal"
              value={formData.plafond}
              onChange={(e) => setFormData({ ...formData, plafond: e.target.value })}
              placeholder="0,00"
            />
          </div>

          <div className="space-y-2">
            <Label>{t('pages.orders.columns.client')}</Label>
            <Select
              value={formData.clientId ? String(formData.clientId) : ''}
              onValueChange={(value) => setFormData({ ...formData, clientId: Number(value) })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('pages.orders.selectClient', 'Select client')} />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={String(client.id)}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('pages.orders.columns.suppliers', 'Fornitore')}</Label>
            <Select
              value={formData.supplierId ? String(formData.supplierId) : ''}
              onValueChange={(value) =>
                setFormData({ ...formData, supplierId: value === '__none__' ? null : Number(value) })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t('pages.orders.selectSupplier', 'Seleziona fornitore...')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t('common.none', 'Nessuno')}</SelectItem>
                {suppliers.map((supplier) => (
                  <SelectItem key={supplier.id} value={String(supplier.id)}>
                    {supplier.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={handleClose} disabled={saving} className="flex-1">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving || !isFormValid()}
              className="flex-1 bg-blue-500 hover:bg-blue-600"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.saving', 'Saving...')}
                </>
              ) : editingOrder ? (
                t('common.save')
              ) : (
                t('common.create')
              )}
            </Button>
          </div>
        </div>
      </BottomSheet>

      {/* Delete confirmation dialog */}
      <BottomSheet
        open={deleteTargetId !== null}
        onClose={() => setDeleteTargetId(null)}
        title={t('pages.orders.confirmDeleteTitle', 'Delete order')}
      >
        <p className="text-sm text-muted-foreground mb-6">{t('pages.orders.confirmDelete')}</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setDeleteTargetId(null)} disabled={deleting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('common.delete', 'Delete')}
          </Button>
        </div>
      </BottomSheet>
    </div>
    </PageTransition>
  );
};

export default OrderManager;
